import { v4 as uuidv4 } from 'uuid'
import L from 'leaflet'
import 'leaflet.heat'
import h337 from '../../../lib/heatmap'

import { post, fetchGet, BlockOrGetData } from '../../../utils/http'
import {
    Category,
    Marker,
    PredictionResponse,
    Annotation,
    GeoJSON,
    GenomicDataObj,
    ColorLibObj,
    HeatPoint,
    HeatMaps,
    SlidePoint
} from '../../../utils/types'
import { decodeRes, generateColorGradient } from '../../../utils/helperFunc'

const calculateArea = (coords: [number, number][]) => {
    let area = 0
    let j = coords.length - 1
    for (let i = 0; i < coords.length; i++) {
        area += (coords[j][0] + coords[i][0]) * (coords[j][1] - coords[i][1])
        j = i
    }
    return Math.abs(area / 2)
}

const processXMLMarkers = (xmlDoc: any, area_needed: boolean, mapCoords: any, colorLib: ColorLibObj[]) => { // To-Do: optimize
    const geoJsonData: GeoJSON = { type: "FeatureCollection", features: [] }
    const annotations = xmlDoc.getElementsByTagName("Annotation")
    const categories: {[key: string]: any} = {};

    let maxHeatValue = -Infinity;
    let isCatgHeat = true;

    for (let i = 0; i < annotations.length; i++) {
        const name = annotations[i].getAttribute("Name");
        if (!categories[name]) {
            categories[name] = { id: uuidv4(), name: name, area: 0 };
            const num = Number(name);
            if (isNaN(num)) isCatgHeat = false;
            else if (num > maxHeatValue) maxHeatValue = num;
        }
    }

    let gradientColors: {[key: string]: any} = {};
    if (isCatgHeat && maxHeatValue !== -Infinity) {
        gradientColors = generateColorGradient(maxHeatValue);
    }

    const colorLibMap = new Map(colorLib.map(c => [c.name.toLowerCase(), c.color]));
    let n = 1;

    for (let i = 0; i < annotations.length; i++) {
        const annotation = annotations[i];
        const name = annotation.getAttribute("Name");
        
        // Resolve color efficiently
        const mappedColor = annotation.getAttribute("Color") 
            || gradientColors[name] 
            || colorLibMap.get(name.toLowerCase()) 
            || null;

        const regions = annotation.getElementsByTagName("Region");
        let currentPolygonFeature: any = null; // Track parent region for holes
        
        for (let j = 0; j < regions.length; j++) {
            const isHole = regions[j].getAttribute("NegativeROA") === "1";
            const vertices = regions[j].getElementsByTagName("Vertex");
            const points: [number, number][] = [];
            const latLng: [number, number][] = [];

            for (let k = 0; k < vertices.length; k++) {
                const x = Number(vertices[k].getAttribute("X"));
                const y = Number(vertices[k].getAttribute("Y"));
                points.push([x, y]);

                const t = mapCoords.unproject([x, y]);
                latLng.push([
                    Math.round(t.lng * 100) / 100, 
                    Math.round(t.lat * 100) / 100
                ]);
            }

            // GeoJSON strict specification requires rings to be closed.
            // If the XML didn't close them, close them here.
            if (latLng.length > 0) {
                const firstP = latLng[0];
                const lastP = latLng[latLng.length - 1];
                if (firstP[0] !== lastP[0] || firstP[1] !== lastP[1]) {
                    latLng.push([...firstP]);
                }
            }

            if (isHole && currentPolygonFeature && !isCatgHeat) {
                // Append the hole to the last created Polygon's geometry
                currentPolygonFeature.geometry.coordinates.push(latLng);
                // Add hole coordinates to properties
                // currentPolygonFeature.properties.holes.push(points);
                // Subtract the hole's area from the parent's total area
                if (area_needed) currentPolygonFeature.properties.area -= calculateArea(points);
            } else {
                // Create a new parent region
                currentPolygonFeature = {
                    type: "Feature",
                    geometry: { type: isCatgHeat ? 'Point' : 'Polygon', coordinates: [latLng] },
                    properties: {
                        id: String(n++),
                        category: name,
                        coordinates: points,
                        area: area_needed ? calculateArea(points) : NaN,
                        color: mappedColor
                    }
                };
                geoJsonData.features.push(currentPolygonFeature);
            }
        }
    }

    let catg: Category[] = Object.values(categories);
    if (isCatgHeat) {
        catg.sort((a, b) => parseInt(a.name) - parseInt(b.name));
    } else {
        catg.sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()));
    }
    
    return { geoJsonData, categories: catg };
}

const getXmlFile = async (key: string, bucket: string, signal?: any) => { // To-Do
    const fileAccess = await post('S3/getaccess', { s3_name: bucket, key: key })
    const res = await BlockOrGetData(fileAccess.url)
    const sizeInMB: number = Number((parseInt((res.headers['content-length'] || ''), 10) / 1048576).toFixed(2))
    return { xml: new DOMParser().parseFromString(res.data, 'text/xml'), size: sizeInMB}
}

const getJsonFile = async (key: string, bucket: string, signal?: any) => {   // To-Do
    const fileAccess = await post('S3/getaccess', { s3_name: bucket, key: key })
    const jsonData = await BlockOrGetData(fileAccess.url)
    return jsonData.data
}

const getModifiedGSData = async (versionId: number) => {
    try {
        const res = await fetchGet(`Annotations/${versionId}`)
        const data = await decodeRes(res)
        
        if (!data || !data.categories) return null

        const safeParse = (data: any, fallback: any) => {
            if (!data) return fallback
            if (typeof data !== 'string') return data // Skip if already an object/array
            try {
                return JSON.parse(data)
            } catch (e) {
                console.error("Failed to parse annotation JSON string:", e)
                return fallback
            }
        }

        data.prediction_result = safeParse(data.prediction_result, {})
        data.custom_markers = safeParse(data.custom_markers, [])
        data.categories = safeParse(data.categories, [])

        return data
    } catch (err) {
        console.error("Error fetching GS Data:", err)
        return null
    }
}

const getSysAnnoData = async (id: number | null, signal: any) => { 
    try { 
        if (!id) throw Error('no sys markers')
        const response = await fetchGet(`PredAnnotation/${id}`, signal)
        const data = await decodeRes(response)
        if ('markers' in data) {
            data.markers = JSON.parse(data.markers)
            data.categories = JSON.parse(data.categories)
            return data
        } else throw Error('Data not found')
    } catch (err) {
        console.log(err)
        return null
    }
}

const calculateDistance = (data: SlidePoint[]) => {
    let s = 0
    try {
        for (let i = 0; i < data.length - 1; i++) {
            const a = data[i].X - data[i + 1].X
            const b = data[i].Y - data[i + 1].Y
            s += Math.hypot(a, b)
        }
    } finally {
        return s
    }
}

const normalizeVolume = (data: number | undefined) => {
    if (!data) return 0
    if (data < 0.01) return Number(data.toFixed(1 - Math.floor(Math.log(data) / Math.log(10))))
    else return Number(data.toFixed(2))
}

const normalizeArea = (data: number | undefined) => {
    if (!data) return 0
    let t = data / 16000000
    return normalizeVolume(t)
}

const processPredictionResults = (predictionResult: PredictionResponse, categories: Category[]): PredictionResponse | {} => {
    try {
        const specialCancerClass = categories.filter(i => i.name.toLowerCase() === 'cancer')
        return {
            tissue_area: normalizeArea(predictionResult.tissue_area),
            cancer_area: normalizeArea(predictionResult.cancer_area),
            tumor_volume: normalizeVolume(predictionResult.tumor_volume),
            tumor_volume_user: specialCancerClass.length ? normalizeVolume((specialCancerClass[0].area / predictionResult.tissue_area!) * 100) : 0,
            gp3: normalizeVolume(predictionResult.gp3),
            gp4: normalizeVolume(predictionResult.gp4),
            gp4_crib: normalizeVolume(predictionResult.gp4_crib),
            gp5: normalizeVolume(predictionResult.gp5),
            pni: predictionResult.pni ? 'Present' : 'Not present',
            hgpin: predictionResult.hgpin ? 'Present' : 'Not present',
            gleason_grade: predictionResult.gleason_grade
        }
    } catch (err) {
        return {}
    }
}

type MergedAnnotation = Marker | Annotation

const mergePolyList = (sysAnnotations: Marker[], userAnnotations: Annotation[]): MergedAnnotation[] => {
    const map = new Map<string, MergedAnnotation>(sysAnnotations.map(i => [i.id, i]))
    userAnnotations.forEach(i => map.set(i.id, {...map.get(i.id), ...i}))
    return Array.from(map.values())
}

const getHeatColor = (value: number, max: number) => {
    try {
      const percent = value / max
      if (percent >= .9) return {r: 255, g: 0, b: 0}  // red
      else if (percent >= .8) return {r: 255, g: 85, b: 0}  // red-yellow
      else if (percent >= .7) return {r: 255, g: 170, b: 0}  // yellow-red
      else if (percent >= .6) return {r: 255, g: 255, b: 0}  // yellow
      else if (percent >= .5) return {r: 170, g: 255, b: 0}  // yellow-green
      else if (percent >= .4) return {r: 85, g: 255, b: 0}  // green-yellow
      else if (percent >= .3) return {r: 0, g: 255, b: 0}  // green
      else if (percent >= .2) return {r: 0, g: 170, b: 85}  // green-blue
      else if (percent >= .1) return {r: 0, g: 85, b: 170}  // blue-green
      else return {r: 0, g: 0, b: 255}  // blue
    } catch (err) {
      return {r: 0, g: 0, b: 255}   // blue
    }
}

// Draw svg image using heatmap data and overlay on top of the image
const drawHeatMap = (ele: HTMLElement, data: HeatPoint[], mapCoords: any, width: number, height: number, max: number) => {
    try {
        if (!ele) throw Error('Container not found')
            
        const maxDimension = 2000;
        const maxWsiDim = Math.max(width, height);
        const scale = maxDimension / maxWsiDim;

        const tW = Math.round(width * scale);
        const tH = Math.round(height * scale);

        ele.style.width = `${tW}px`
        ele.style.height = `${tH}px`

        const heatmapInstance = h337.create({
            container: ele,
            useLocalExtrema: false,
            // minOpacity: 0.2,
            // maxOpacity: 0.8,
            blur: 1,
            gradient: {
                '0.1': 'blue',
                '0.3': 'lime',
                '0.5': 'yellow',
                '0.75': 'orange',
                '1': 'red'
            }
        })

        heatmapInstance.setData({ max: max, data: data });
        const heatImage = heatmapInstance.getDataURL()

        const southWest = mapCoords.unproject([0, height])
        const northEast = mapCoords.unproject([width, 0])
        const bounds = L.latLngBounds(southWest, northEast)

        return L.imageOverlay(heatImage, bounds, { zIndex: 99999 })
    } catch (err) {
        console.log(err)
        return null
    }
}

// BCR Heatmap Data
const processBcrHeatData = (geojsonData: GeoJSON, width: number, height: number) => {
    // Normalize scaling factor using the longest slide dimension
    const maxDimension = 2000
    const maxWsiDim = Math.max(width, height)
    const scale = maxDimension / maxWsiDim;

    const canvasRadius = Math.max(5, Math.round(maxDimension * 0.005))

    return geojsonData.features.map(feature => {
        const prop = feature.properties;
        const coords = prop.coordinates;
        
        let value = 0.5;
        if (prop.category === 'HIGH') value = 1.0;
        else if (prop.category === 'LOW') value = 0.1;

        let rawX = 0, rawY = 0;
        if (coords.length === 1) {
            rawX = Number(coords[0][0]);
            rawY = Number(coords[0][1]);
        } else {
            let sumX = 0, sumY = 0;
            for (let i = 0; i < coords.length; i++) {
                sumX += Number(coords[i][0]);
                sumY += Number(coords[i][1]);
            }
            rawX = sumX / coords.length;
            rawY = sumY / coords.length;
        }

        return {
            x: Math.round(rawX * scale),
            y: Math.round(rawY * scale),
            value,
            radius: canvasRadius
        };
    });
};

// Genomic Heatmap Data
const processGeneHeatData = (data: GenomicDataObj, gene: string, width: number, height: number) => {
    const tW = 1000;
    const tH = 1000 / (width / height);
    const scaleX = tW / width;
    const scaleY = tH / height;

    const geneIndex = data.gene_name.findIndex(i => i === gene);
    if (geneIndex === -1) return { heatValues: [], max: 0 }; // Added safety guard
    
    const targetGeneData = data.spatial_gene_expression[geneIndex];
    const max = targetGeneData.max_value;
    const heatVals = targetGeneData.heat_values;
    const grid = data.grid;
    const len = grid.length;

    const heatValues = new Array(len);

    for (let i = 0; i < len; i++) {
        const val = heatVals[i];
        const rawRadius = Math.max((val / max) * 8.25, 5);

        heatValues[i] = {
            x: Math.round((Number(grid[i][0]) * scaleX) * 1000) / 1000,
            y: Math.round((Number(grid[i][1]) * scaleY) * 1000) / 1000,
            value: val,
            radius: Math.round(rawRadius * 100) / 100
        };
    }

    return { heatValues, max };
}

// JSON heatmap data
const processJsonHeatmap = (data: HeatMaps[], width: number, height: number): {[x: string]: HeatMaps} => {
    const tW: number = 1000;
    const tH: number = 1000 / (width / height);
    const scaleX = tW / width;
    const scaleY = tH / height;
    const defaultRadius: number = Math.ceil(Math.min(width, height) * 0.00005);
    const res: {[x: string]: HeatMaps} = {};

    for (let i = 0; i < data.length; i++) {
        const mapItem = data[i];
        const max = mapItem.max_value;

        res[mapItem.name] = {
            ...mapItem,
            points: mapItem.points.map(j => {
                let r = j.radius;
                if (!r) {
                    const computedR = Math.max((j.value / max) * 5.35, 4);
                    r = Math.round(computedR * 100) / 100;
                }

                return {
                    x: Math.round((Number(j.x) * scaleX) * 1000) / 1000,
                    y: Math.round((Number(j.y) * scaleY) * 1000) / 1000,
                    value: j.value,
                    radius: r || defaultRadius
                };
            })
        };
    }

    return res;
}

const createGeojsonDataFromMarkerList = (data: MergedAnnotation[], colorLib: ColorLibObj[], mapCoords :any) => {
    const geoJsonData: GeoJSON = { type: "FeatureCollection", features: [] }
    
    for (let i of data) {
        if (i.is_deleted) continue
        if (Array.isArray(i.coordinates)) {
            if (!i.category) continue
            // polygons
            const latLng: [number, number][] = []
            for (let j of i.coordinates) {
                const point = mapCoords.unproject([j.X, j.Y])
                latLng.push([point.lng.toFixed(2), point.lat.toFixed(2)])
            }
            const color = colorLib.find(x => x.name.toLowerCase() === String(i.category).toLowerCase())
            geoJsonData.features.push({
                type: "Feature",
                geometry: { type: 'Polygon', coordinates: [latLng] },
                properties: { id: i.id, category: i.category, coordinates: [], color: color ? color.color : null }
            })
        } else {
            // circle marker or point
            const point = mapCoords.unproject([i.coordinates.X, i.coordinates.Y])
            geoJsonData.features.push({
                type: "Feature",
                geometry: { type: 'Point', coordinates: [point.lng.toFixed(2), point.lat.toFixed(2)] },
                properties: { id: i.id, coordinates: [], color: null }
            })
        }
    }

    return geoJsonData
}

export {
    getSysAnnoData,
    getModifiedGSData,
    getXmlFile,
    getJsonFile,
    processXMLMarkers,
    calculateDistance,
    processPredictionResults,
    normalizeArea,
    mergePolyList,
    drawHeatMap,
    processBcrHeatData,
    getHeatColor,
    processGeneHeatData,
    createGeojsonDataFromMarkerList,
    processJsonHeatmap
}